import { types } from 'utils';

export const geocode = async (lat: number, lng: number) => {
	try {
		const geocoder = new (window as any).google.maps.Geocoder();
		const { results } = await geocoder.geocode({ location: { lat, lng } });
		return results;
	} catch (error) {
		return { status: 606, message: 'Network request failed', error };
	}
};

export const autocomplete = async (input: string): Promise<types.Address[]> => {
	try {
		const { suggestions } = await (
			window as any
		).google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
			input,
			// types: ['address'],
			includedRegionCodes: ['ng', 'gh'],
		});
		return (suggestions || []).map((s: any) => {
			return {
				id: s.placePrediction.placeId,
				name: s.placePrediction.text.text,
			};
		}) as types.Address[];
	} catch (error) {
		return [];
	}
};

export const placeDetails = async (
	placeId: string
): Promise<types.Address | null> => {
	try {
		const place = new (window as any).google.maps.places.Place({
			id: placeId,
		});
		await place.fetchFields({
			fields: [
				'displayName',
				'location',
				'formattedAddress',
				'addressComponents',
				'businessStatus',
			],
		});
		return addressComponents({
			name: place.displayName,
			formatted_address: place.formattedAddress,
			business_status: place.businessStatus,
			geometry: place.location ? { location: place.location } : undefined,
			address_components: (place.addressComponents || []).map((c: any) => ({
				long_name: c.longText,
				short_name: c.shortText,
				types: c.types,
			})),
		});
	} catch (error) {
		return null;
	}
};

// Buildings (university, hospital, ...) are identified by their name, so
// they don't need a house number or street name. Everything else does.
export const isAddressComplete = (address?: types.Address | null) => {
	if (!address || !address.x || !address.y) return false;
	if (address.kind === 'building') return true;
	return !!address.houseNo && !!address.street;
};

// Asks the user for the house number and street name an address is missing.
// Resolves with the completed address, or null if they cancel.
export const promptAddressDetails = (
	presentAlert: (options: any) => Promise<void>,
	address: types.Address
): Promise<types.Address | null> => {
	return new Promise((resolve) => {
		presentAlert({
			header: 'Almost there!',
			message: 'Add your house number and street name for faster delivery.',
			inputs: [
				{
					name: 'houseNo',
					type: 'text',
					placeholder: 'House number',
					value: address.houseNo || '',
					autocomplete: 'off',
				},
				{
					name: 'street',
					type: 'text',
					placeholder: 'Street name',
					value: address.street || '',
					autocomplete: 'off',
				},
			],
			buttons: [
				{ text: 'Cancel', role: 'cancel', handler: () => resolve(null) },
				{
					text: 'Save',
					handler: (v: { houseNo?: string; street?: string }) => {
						const houseNo = (v.houseNo || '').trim();
						const street = (v.street || '').trim();
						if (!houseNo || !street) return false; // keep the alert open
						resolve({ ...address, kind: 'home', houseNo, street });
					},
				},
			],
			onDidDismiss: () => resolve(null), // backdrop/escape; no-op once resolved
		});
	});
};

export const addressComponents = (plc: any): types.Address | null => {
	if (!plc || !plc.geometry || !plc.address_components) return null;
	const findComponentName = (
		type: string,
		key: 'short_name' | 'long_name' = 'long_name'
	) => {
		const name: string =
			((plc.address_components || []).find((pa: any) =>
				(pa.types || []).includes(type)
			) || {})[key] || '';
		return name;
	};
	const kind: types.AddressKind = plc.business_status
		? 'building'
		: findComponentName('street_number')
			? 'home'
			: findComponentName('route')
				? 'street'
				: 'area';
	return {
		kind,
		x:
			typeof plc.geometry?.location?.lat === 'number'
				? plc.geometry?.location?.lat
				: plc.geometry?.location?.lat(),
		y:
			typeof plc.geometry?.location?.lng === 'number'
				? plc.geometry?.location?.lng
				: plc.geometry?.location?.lng(),
		iso: findComponentName('country', 'short_name'),
		city: findComponentName('locality'),
		name: `${plc.name ? `${plc.name}, ` : ''}${plc.formatted_address ? plc.formatted_address.replace(`${plc.name}, `, ``) : ''}`,
		state: findComponentName('administrative_area_level_1'),
		street:
			findComponentName('route') ||
			findComponentName('neighborhood') ||
			findComponentName('sublocality') ||
			findComponentName('premise') ||
			plc.name ||
			'',
		country: findComponentName('country'),
		houseNo: findComponentName('street_number'),
		timeZone: '',
		zoom: 15,
	};
};

// Picks the most precise reverse-geocode result: rooftop/street-level matches
// first, skipping plus codes. Falls back to the first result.
const bestGeocodeResult = (results: any[]) => {
	const usable = results.filter(
		(r) => !(r.types || []).includes('plus_code') && r.address_components
	);
	const rank = (r: any) => {
		const t: string[] = r.types || [];
		if (t.includes('street_address') || t.includes('premise')) return 0;
		if (r.geometry?.location_type === 'ROOFTOP') return 1;
		if (t.includes('route')) return 2;
		return 3;
	};
	return [...usable].sort((a, b) => rank(a) - rank(b))[0] || results[0];
};

export const myCurrentLocation = (): Promise<types.Address | null> => {
	return new Promise((resolve) => {
		if (!navigator.geolocation) return resolve(null);
		navigator.geolocation.getCurrentPosition(
			async (position: GeolocationPosition) => {
				const { latitude: lat, longitude: lng } = position.coords;
				const results = await geocode(lat, lng);
				if (!Array.isArray(results) || !results.length) return resolve(null);
				const address = addressComponents(bestGeocodeResult(results));
				// keep the user's real position, not the geocoder's snapped point
				resolve(address ? { ...address, x: lat, y: lng } : null);
			},
			(e) => {
				console.log(e);
				resolve(null);
			},
			{ enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
		);
	});
};
